import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { platformAPI } from '../../services/api';
import PaginationBar from '../../components/common/PaginationBar';

const AdminPlatforms = () => {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false
  });
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  const fetchPlatforms = async (page = 0, term = '') => {
    setLoading(true);
    try {
      const response = await platformAPI.getPlatforms({
        page: page,
        size: pagination.pageSize,
        search: term
      });
      const data = response.data;
      
      // Handle different response structures
      if (Array.isArray(data)) {
        // Direct array response
        const startIndex = page * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedData = data.slice(startIndex, endIndex);
        
        setPlatforms(paginatedData);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalPages: Math.ceil(data.length / prev.pageSize),
          totalItems: data.length,
          hasNext: endIndex < data.length,
          hasPrevious: page > 0
        }));
      } else if (data.data) {
        // Paginated response with data wrapper
        setPlatforms(data.data);
        setPagination(prev => ({
          ...prev,
          currentPage: data.pageNumber || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalElements || 0,
          hasNext: data.hasNext || false,
          hasPrevious: data.hasPrevious || false
        }));
      } else if (data.content) {
        // Spring pagination style
        setPlatforms(data.content);
        setPagination(prev => ({
          ...prev,
          currentPage: data.number || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalElements || 0,
          hasNext: data.hasNext || false,
          hasPrevious: data.hasPrevious || false
        }));
      } else {
        // Fallback
        setPlatforms(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
      toast.error('Failed to load platforms');
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPlatforms(0, searchTerm);
  };

  const handlePageChange = (page) => {
    fetchPlatforms(page, searchTerm);
  };

  const handleView = async (platform) => {
    try {
      const response = await platformAPI.getPlatform(platform.id);
      setSelectedPlatform(response.data.data || response.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to fetch platform details');
    }
  };

  const handleEdit = async (platform) => {
    try {
      const response = await platformAPI.getPlatform(platform.id);
      const platformData = response.data.data || response.data;
      setSelectedPlatform(platformData);
      setEditFormData(platformData);
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to fetch platform details');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await platformAPI.updatePlatform(selectedPlatform.id, editFormData);
      toast.success('Platform updated successfully');
      setShowEditModal(false);
      fetchPlatforms(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update platform');
    }
  };

  const handleDelete = async () => {
    try {
      await platformAPI.deletePlatform(selectedPlatform.id);
      toast.success('Platform deleted successfully');
      setShowDeleteModal(false);
      fetchPlatforms(pagination.currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete platform');
    }
  };

  const handleDropdownToggle = (platformId) => {
    setActiveDropdown(activeDropdown === platformId ? null : platformId);
  };

  const handleDropdownAction = (action, platform) => {
    setActiveDropdown(null);
    switch(action) {
      case 'view':
        handleView(platform);
        break;
      case 'edit':
        handleEdit(platform);
        break;
      case 'delete':
        setSelectedPlatform(platform);
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      Active: { color: '#10b981', bg: '#d1fae5', label: 'Active' },
      Inactive: { color: '#6b7280', bg: '#f3f4f6', label: 'Inactive' }
    };
    return config[status] || { color: '#6b7280', bg: '#f3f4f6', label: status };
  };

  const Pagination = () => (
    <PaginationBar
      page={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalElements={pagination.totalItems}
      pageSize={pagination.pageSize}
      onPageChange={handlePageChange}
      style={{ padding: '20px 24px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}
    />
  );

  return (
    <div className="admin-page-shell" style={{ 
      padding: '16px',
      flex: 1,
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backgroundColor: '#f8fafc',
        paddingBottom: '16px'
      }}>
        <div className="admin-page-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>Platform Management</h2>
          <Button 
            icon="plus"
            onClick={() => navigate('/admin/create-platform')}
          >
            Add Platform
          </Button>
        </div>

        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: '12px', maxWidth: '500px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Icon 
                name="search" 
                size={16} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} 
              />
              <input
                type="text"
                placeholder="Search by platform name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  background: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              />
            </div>
            <Button type="submit" disabled={loading}>
              Search
            </Button>
          </div>
        </form>
      </div>
      
      <div className="admin-table-wrap" style={{ 
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          background: '#ffffff', 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0', 
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
          <div className="table-scroll-container" style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Platform Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '3px solid var(--bg-deep)', borderTop: '3px solid var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <div style={{ marginTop: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>Loading platforms...</div>
                  </td>
                </tr>
              ) : platforms.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🏪</div>
                    <div style={{ fontSize: '16px', color: 'var(--text-muted)', marginBottom: '8px' }}>No platforms found</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Try adjusting your search or add a new platform</div>
                  </td>
                </tr>
              ) : (
                platforms.map((platform) => (
                  <tr 
                    key={platform.id} 
                    style={{ 
                      borderBottom: '1px solid #f1f5f9'
                    }}
                  >
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500' }}>{platform.platformName}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', backgroundColor: getStatusBadge(platform.active ? 'Active' : 'Inactive').bg, color: getStatusBadge(platform.active ? 'Active' : 'Inactive').color }}>
                        {platform.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ position: 'relative' }}>
                        <button 
                          onClick={() => handleDropdownToggle(platform.id)} 
                          style={{ 
                            padding: '6px 12px', 
                            background: '#3b82f6', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            fontSize: '12px', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                        >
                          More
                          <Icon name="chevron-down" size={14} />
                        </button>
                        {activeDropdown === platform.id && (
                          <div style={{ 
                            position: 'absolute', 
                            left: 0, 
                            top: '100%', 
                            marginTop: '4px',
                            background: '#ffffff', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            zIndex: 20, 
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                            padding: '4px', 
                            minWidth: '120px' 
                          }}>
                            <button onClick={() => handleDropdownAction('view', platform)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Icon name="eye" size={14} style={{ color: '#3b82f6', opacity: 0.8 }} /> View
                            </button>
                            <button onClick={() => handleDropdownAction('edit', platform)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Icon name="edit-2" size={14} style={{ color: '#10b981', opacity: 0.8 }} /> Edit
                            </button>
                            <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                            <button onClick={() => handleDropdownAction('delete', platform)} style={{ width: '100%', padding: '8px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
                              <Icon name="trash-2" size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div style={{ flexShrink: 0 }}>
          <Pagination />
        </div>
      </div>
    </div>

    {/* View Modal */}
    {showViewModal && selectedPlatform && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>Platform Details</h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>Viewing full information for this platform</p>
            </div>
            <button
              onClick={() => setShowViewModal(false)}
              style={{ 
                background: 'var(--bg-deep)', 
                border: 'none', 
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer', 
                color: 'var(--text-muted)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--red)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              <Icon name="x" size={20} />
            </button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
            <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Platform Name</label>
              <div style={{ fontSize: '16px', color: 'var(--text)', fontWeight: '600' }}>{selectedPlatform.platformName}</div>
            </div>

            <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Description</label>
              <div style={{ fontSize: '14px', color: 'var(--text)', lineHeight: '1.6' }}>{selectedPlatform.description || 'No description provided.'}</div>
            </div>

            <div style={{ padding: '16px', background: 'var(--bg-deep)', borderRadius: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Platform Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  background: selectedPlatform.active ? '#10b981' : '#ef4444' 
                }} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: selectedPlatform.active ? '#10b981' : '#ef4444'
                }}>
                  {selectedPlatform.active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button
              variant="secondary"
              onClick={() => setShowViewModal(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Edit Modal */}
    {showEditModal && selectedPlatform && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>Edit Platform</h3>
            <button
              onClick={() => setShowEditModal(false)}
              style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleEditSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Platform Name *</label>
                <input
                  type="text"
                  value={editFormData.platformName || ''}
                  onChange={(e) => setEditFormData({...editFormData, platformName: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    background: '#ffffff',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Status</label>
                <select
                  value={editFormData.active ? 'true' : 'false'}
                  onChange={(e) => setEditFormData({...editFormData, active: e.target.value === 'true'})}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    background: '#ffffff',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {showDeleteModal && selectedPlatform && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>Delete Platform</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
              Are you sure you want to delete <strong>{selectedPlatform.platformName}</strong>? This action cannot be undone.
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    )}

    
    <style jsx>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>
    </div>
  );
};

export default AdminPlatforms;
